import {
  Anchor,
  Avatar,
  Box,
  Button,
  Card,
  Center,
  CloseIcon,
  Flex,
  Group,
  Menu,
  Modal,
  rem,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { Dropzone, FileWithPath, MIME_TYPES } from '@mantine/dropzone';
import { useDisclosure } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { useNavigate, useSearchParams } from '@remix-run/react';

import debounce from 'debounce';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyList } from '~/components/EmptyList';
import { ExportMembers } from '~/components/ExportMembers/ExportMembers';
import {
  AddRounded,
  CancelRounded,
  CheckCircleRounded,
  DownloadRounded,
  ExpandMoreRounded,
  FilterListRounded,
  MoreVertRounded,
  SearchRounded,
  UploadFileRounded,
} from '~/components/icons';
import { MultiSelectDropdown } from '~/components/Input/MultiSelectDropdown';
import { MultiSelectDropdownOptionProp } from '~/components/Input/MultiSelectDropdown/MultiSelectDropdown';
import Notify from '~/components/Notify/Notify';
import { SearchResult } from '~/components/SearchResult';
import {
  PaginationComponent,
  TableComponent,
} from '~/components/TableComponent';
import { TableProps } from '~/components/TableComponent/TableComponent';
import Body1 from '~/components/Typograph/Body1';
import Body2 from '~/components/Typograph/Body2';
import Caption from '~/components/Typograph/Caption';
import { countryList } from '~/constants';
import { useUserBlock, useUserListPagination } from '~/services/user';
import { UserBlockRequest } from '~/services/user/UserBlock';
import { useUserImport } from '~/services/user/UserImport';
import {
  UserImportInspectResponse,
  useUserImportInspect,
} from '~/services/user/UserImportInspect';
import { useUserImportTemplate } from '~/services/user/UserImportTemplate';
import { UserDetail } from '~/services/user/UserList';
import { formatDate } from '~/utils/format';
import { HttpData } from '~/utils/network/HttpData';
import { MemberStatusComponent } from '.';
import { memberStatusOptions, sortOptions, userTypeOptions } from './options';

export default function MemberTable() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [searchParams, setParams] = useSearchParams();

  const [q, setQ] = useState(searchParams.get('q') ?? '');
  const [filterLocations, setFilterLocations] = useState(
    searchParams.get('locations') ?? ''
  );

  const [page, setPage] = useState(searchParams.get('page') ?? '1');
  const [limit, setLimit] = useState(searchParams.get('limit') ?? '10');

  const [selectedSortBy, setSelectedSortBy] = useState(
    searchParams.get('sort_by') || 'created_at-DESC'
  );
  const [statusMember, setStatusMember] = useState(searchParams.get('status'));
  const [userType, setUserType] = useState(
    searchParams.get('user_type') ?? 'all'
  );
  const [locationOptions, setLocationOptions] = useState<
    MultiSelectDropdownOptionProp[]
  >([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [modalImportMemberOpen, { toggle: toggleImportMemberOpen }] =
    useDisclosure(false);

  const [
    modalExportMemberOpen,
    { toggle: toggleExportMemberOpen, close: closeExportMember },
  ] = useDisclosure(false);

  // section import member
  const { data: fileBlob } = useUserImportTemplate();
  const [files, setFiles] = useState<FileWithPath[]>([]);
  const [isFileInvalid, setIsFileInvalid] = useState<boolean | null>(null);
  const [importInspection, setImportInspection] =
    useState<UserImportInspectResponse | null>(null);

  const { mutateAsync: mutateImportInspect } = useUserImportInspect({
    onError: () => {
      Notify.error({
        message: t('general-component.error.failed_import_inspect'),
      });
    },
    onSuccess: (data: HttpData<UserImportInspectResponse>) => {
      setImportInspection(data.data);
    },
  });

  const { mutateAsync: mutateImport } = useUserImport({
    onError: () => {
      Notify.error({
        message: t('manage_members.label.failed_import_user'),
      });
    },
    onSuccess: () => {
      Notify.success({
        message: t('manage_members.label.success_import_user'),
      });
      toggleImportMemberOpen();
      refetch();
    },
  });

  useEffect(() => {
    if (!locationOptions.length) {
      setLocationOptions(countryList.map((c) => ({ label: c, value: c })));
    }
  }, []);

  const handleLocationsChange = async (
    values: MultiSelectDropdownOptionProp[]
  ) => {
    const d = values
      .filter((v) => v.checked)
      .map((s) => s.label)
      .join(',');
    setFilterLocations(d);
    if (d.length) {
      searchParams.set('locations', d);
    } else {
      searchParams.delete('locations');
    }
    setParams(searchParams);
    await refetch();
    await refetch();
  };

  const {
    data,
    isLoading = true,
    refetch,
  } = useUserListPagination({
    payload: {
      q: q ?? '',
      page: +parseInt(page ?? '1'),
      limit: parseInt(limit ?? '10'),
      status: statusMember ?? '',
      user_type: userType !== 'all' ? userType : '',
      sort_by: selectedSortBy.split('-')[0],
      sort_order: selectedSortBy.split('-')[1],
      location: filterLocations,
    },
  });

  const { mutateAsync: mutateBlockUser } = useUserBlock({
    onError: () => {
      Notify.error({
        message: t('general-component.error.failed_block_unblock_user'),
      });
    },
    onSuccess: async (resp) => {
      let message = t('general-component.response.success_unblock_user');
      if (resp.data?.is_blocked) {
        message = t('general-component.response.success_block_user');
      }
      Notify.success({
        message: (
          <>
            <Text component="span" fw="bold" c="var(--success-dark)">
              {message}
            </Text>
          </>
        ),
      });
      refetch();
    },
  });

  const blockUser = (id: number, block: boolean) => {
    const payload: UserBlockRequest = {
      id,
      is_blocked: block,
    };
    mutateBlockUser(payload as never);
  };

  const openBlockConfirmModal = (user: UserDetail, block: boolean) =>
    modals.openConfirmModal({
      title: `${block ? 'Block' : 'Unblock'} ${user.name} ?`,
      children: (
        <Caption c="var(--text-secondary)">
          {block
            ? "Are you sure you want to block this member ? Blocking this member will restrict their access to the platform and any associated resources until they're unblocked"
            : 'Are you sure you want to unblock this member? Unblocking this member will restore their access to the platform and associated resources.'}
        </Caption>
      ),
      labels: {
        confirm: block ? 'Block Member' : 'Unblock Member',
        cancel: 'Cancel',
      },
      confirmProps: {
        color: block ? 'var(--error-light)' : 'var(--primary-main)',
      },
      onCancel: () => {},
      onConfirm: () => {
        blockUser(user.id, block);
      },
    });

  const tableOptions: TableProps<UserDetail> = useMemo(
    () => ({
      data: data?.pages?.[0].data ?? [],
      isSelectable: false,
      isLoading,
      tableName: 'manage-members-table',
      emptyResultTitle: 'No members found',
      emptyResultDescription: 'No members found',
      columns: [
        {
          id: 'name',
          label: 'Member',
          render(rowData) {
            return (
              <Flex gap={rem(10)}>
                <Avatar src={rowData.avatar_url}>{rowData.name[0]}</Avatar>

                <Stack gap={5}>
                  <Anchor
                    c="var(--primary-main)"
                    fw={600}
                    underline="never"
                    onClick={() => navigate('./' + rowData.username)}
                  >
                    {rowData.name}
                  </Anchor>
                  <Caption c="var(--text-tertiary)">
                    @{rowData.username}
                  </Caption>
                </Stack>
              </Flex>
            );
          },
        },
        {
          id: 'email',
          label: 'Email Address',
          render(rowData) {
            if (!rowData.contact_email) return rowData.email;
            return (
              <Stack gap={5}>
                <Body2 c="var(--text-secondary)">{rowData.contact_email}</Body2>
                <Caption c="var(--text-tertiary)">{rowData.email}</Caption>
              </Stack>
            );
          },
        },
        {
          id: 'location',
          label: 'Location',
        },
        {
          id: 'job_title',
          label: 'Job Title',
          render(rowData) {
            return (
              <Stack gap={5}>
                <Body2 c="var(--text-secondary)">{rowData.job_title}</Body2>
                <Caption c="var(--text-tertiary)">
                  {rowData.years_of_experience
                    ? `${rowData.years_of_experience} Years`
                    : '-'}
                </Caption>
              </Stack>
            );
          },
        },
        {
          id: 'is_receive_notification',
          label: 'Marketing Email',
          render(rowData) {
            return (
              <Center>
                {rowData.is_receive_notification && (
                  <CheckCircleRounded fill="var(--primary-main)" />
                )}
                {!rowData.is_receive_notification && (
                  <CancelRounded fill="var(--error-main)" />
                )}
              </Center>
            );
          },
        },
        {
          id: 'last_active_at',
          label: 'Last Active',
          render(rowData) {
            const formattedDate = formatDate(
              rowData.last_active_at,
              'DD MMM YYYY'
            );
            return formattedDate === '01 Jan 0001' ? '-' : formattedDate;
          },
        },
        {
          id: 'status',
          label: 'Status',
          render(rowData) {
            return <MemberStatusComponent status={rowData.status} />;
          },
        },
        {
          id: '',
          label: 'Action',
          render(rowData) {
            return (
              <Menu shadow="md" position="bottom-end">
                <Menu.Target>
                  <Button variant="transparent">
                    <MoreVertRounded fill="var(--action-active)" />
                  </Button>
                </Menu.Target>

                <Menu.Dropdown>
                  <Menu.Item
                    onClick={() => {
                      navigate(`./${rowData.id}/update`);
                    }}
                  >
                    {t('general-component.button.update_profile')}
                  </Menu.Item>
                  {rowData.status === 'invited' && (
                    <Menu.Item
                    // c="var(--error-main)"
                    >
                      {t('manage_members.button.resend_invitation')}
                    </Menu.Item>
                  )}
                  {rowData.status === 'deactivated' ? (
                    <Menu.Item
                      onClick={() => {
                        openBlockConfirmModal(rowData, false);
                      }}
                    >
                      {t('general-component.button.unblock_member')}
                    </Menu.Item>
                  ) : (
                    <Menu.Item
                      c="var(--error-main)"
                      onClick={() => {
                        openBlockConfirmModal(rowData, true);
                      }}
                    >
                      {t('general-component.button.block_member')}
                    </Menu.Item>
                  )}
                </Menu.Dropdown>
              </Menu>
            );
          },
        },
      ],
    }),
    [data, isLoading]
  );

  const isFiltering =
    q !== '' ||
    filterLocations !== '' ||
    statusMember !== null ||
    userType !== 'all';

  const resetFilter = async () => {
    setParams();
    setQ('');
    setFilterLocations('');
    setStatusMember(null);
    setUserType('all');
    setSelectedSortBy('created_at-DESC');

    await refetch();
    await refetch();
  };

  const handleSearch = useCallback(
    debounce(async () => {
      setPage('1');
      searchParams.set('page', '1');
      setParams(searchParams);
      await refetch();
      await refetch();
    }, 400),
    [refetch]
  );

  // When you want to trigger the download
  const handleDownload = () => {
    if (fileBlob) {
      const url = window.URL.createObjectURL(fileBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'template.csv'; // or whatever filename you expect
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    }
  };

  const handleFileUpload = async () => {
    if (files.length > 0) {
      await mutateImportInspect({ file: files[0] });
    }
  };

  const handleImport = async () => {
    if (files.length > 0) {
      await mutateImport({ file: files[0] });
    }
  };

  return (
    <>
      <Modal
        opened={modalExportMemberOpen}
        onClose={() => {
          closeExportMember();
        }}
        title="Export List"
      >
        <ExportMembers
          onClose={closeExportMember}
          exportMembers={() => {
            console.log('Clicked');
          }}
          isExporting={false}
        />
      </Modal>
      <Modal
        opened={modalImportMemberOpen}
        size="60%"
        onClose={() => toggleImportMemberOpen()}
        title="Import Members from CSV File"
        centered
        styles={{
          title: {
            fontSize: '20px',
          },
        }}
      >
        {!importInspection && (
          <Flex direction="column" gap={16}>
            <Text size="16px" onClick={handleDownload}>
              Download the <Anchor href="#">sample CSV template</Anchor>
            </Text>
            <Dropzone
              onDrop={(e) => {
                setFiles(e);
              }}
              maxSize={3 * 1024 ** 2}
              accept={[MIME_TYPES.csv, MIME_TYPES.xls, MIME_TYPES.xlsx]}
              style={{ cursor: 'pointer' }}
              styles={{
                inner: {
                  border: '2px dashed rgba(0, 0, 0, 0.12)',
                },
              }}
              validator={(v) => {
                if (v.size > 3000000) {
                  setIsFileInvalid(true);
                  return null;
                }
                return null;
              }}
            >
              <Stack
                justify="center"
                align="center"
                gap="lg"
                mih={190}
                style={{ pointerEvents: 'none' }}
              >
                <Dropzone.Idle>
                  <UploadFileRounded fill="var(--primary-main)" />
                </Dropzone.Idle>

                <Stack align="center" gap={8}>
                  <Flex gap={5}>
                    <Anchor variant="transparent">
                      <Body1 size="xl">Click to upload</Body1>
                    </Anchor>
                    <Body1 c="var(--text-primary)">or Drag and drop</Body1>
                  </Flex>
                  <Body2 size="xl" c="var(--text-secondary)">
                    CSV, XLS, XLSX (max 3MB)
                  </Body2>
                </Stack>
              </Stack>
            </Dropzone>
            {files.length > 0 && (
              <Flex direction="row" justify="space-between" gap={8}>
                <Flex direction="row" gap={8}>
                  <UploadFileRounded fill="var(--primary-main)" />
                  <Flex direction="column">
                    <Text fz="16px" fw="400" c="var(--text-primary)">
                      {files[0].name}
                    </Text>
                    <Caption c="var(--text-secondary)">
                      {files[0].size} bytes
                    </Caption>
                  </Flex>
                </Flex>
                <Button variant="transparent" onClick={() => setFiles([])}>
                  <CloseIcon color="var(--action-active)" size="20px" />
                </Button>
              </Flex>
            )}
            <Flex direction="row" justify="flex-end" gap={8}>
              <Button
                variant="outline"
                onClick={() => toggleImportMemberOpen()}
              >
                {t('general-component.button.cancel')}
              </Button>
              <Button onClick={handleFileUpload}>Import Members</Button>
            </Flex>
          </Flex>
        )}

        {importInspection && (
          <Flex direction="column" gap={16} mt={16}>
            <Card shadow="sm">
              <Text c="var(--primary-main)" size="20px" mb={10}>
                Valid Emails
              </Text>
              <Text c="var(--text-secondary)" size="16px" mb={4}>
                {importInspection.valid_emails.length} emails are valid
              </Text>
              <Text c="var(--text-secondary)" size="16px">
                {importInspection.valid_emails.join(', ')}
              </Text>
            </Card>

            {importInspection.duplicate_emails.length > 0 && (
              <Card shadow="sm">
                <Text c="var(--error-main)" size="20px" mb={10}>
                  Duplicate Emails Detected
                </Text>
                <Text c="var(--text-secondary)" size="16px" mb={4}>
                  {importInspection.duplicate_emails.length} duplicate emails
                  found
                </Text>
                <Text c="var(--text-secondary)" size="16px">
                  {importInspection.duplicate_emails.join(', ')}
                </Text>
              </Card>
            )}

            {importInspection?.invalid_or_missing_emails?.length > 0 && (
              <Card shadow="sm">
                <Text c="var(--error-main)" size="20px" mb={10}>
                  Invalid or Missing Required Fields
                </Text>
                <Text c="var(--text-secondary)" size="16px" mb={4}>
                  {importInspection.invalid_or_missing_emails.length} invalid
                  emails found
                </Text>
                <Text c="var(--text-secondary)" size="16px">
                  {importInspection.invalid_or_missing_emails.join(', ')}
                </Text>
              </Card>
            )}

            <Flex direction="row" justify="space-between" gap={8}>
              <Button
                variant="outline"
                onClick={() => toggleImportMemberOpen()}
              >
                Cancel
              </Button>

              <Flex direction="row" gap={8}>
                <Button
                  variant="outline"
                  onClick={() => {
                    setFiles([]);
                    setImportInspection(null);
                  }}
                >
                  Re-upload CSV
                </Button>
                <Button onClick={handleImport}>
                  Continue to Add Valid Email
                </Button>
              </Flex>
            </Flex>
          </Flex>
        )}
      </Modal>
      <Stack>
        <Group gap={16} wrap="nowrap">
          <Box pb={5} flex={1} miw={180}>
            <TextInput
              size="md"
              flex={1}
              value={q}
              leftSection={<SearchRounded fill="var(--action-active)" />}
              placeholder={t('manage-forums.label.search')}
              onChange={async (e) => {
                setQ(e.target.value);
                if (e.target.value) {
                  searchParams.set('q', e.target.value);
                } else {
                  searchParams.delete('q');
                }
                setParams(searchParams);
                handleSearch();
              }}
            />
          </Box>

          <Box pb={5} w={150}>
            <MultiSelectDropdown
              defaultValues={filterLocations}
              availableOptions={locationOptions}
              onChange={handleLocationsChange}
              label="Location"
            />
          </Box>

          <Select
            w={150}
            data={memberStatusOptions(t)}
            value={statusMember}
            rightSection={<ExpandMoreRounded fill="var(--action-active)" />}
            placeholder={t('manage-forums.label.status')}
            rightSectionPointerEvents="none"
            onChange={async (e) => {
              setStatusMember(e);
              if (e) {
                searchParams.set('status', e);
              } else {
                searchParams.delete('status');
              }
              setParams(searchParams);
              await refetch();
              await refetch();
            }}
          />
          <Select
            w={160}
            data={userTypeOptions(t)}
            value={userType}
            rightSection={<ExpandMoreRounded fill="var(--action-active)" />}
            placeholder={t('manage_members.label.user_type')}
            rightSectionPointerEvents="none"
            onChange={async (e) => {
              const next = e ?? 'all';
              setUserType(next);
              if (next !== 'all') {
                searchParams.set('user_type', next);
              } else {
                searchParams.delete('user_type');
              }
              setParams(searchParams);
              await refetch();
              await refetch();
            }}
          />
          <Menu shadow="md">
            <Menu.Target>
              <Button variant="transparent">
                <FilterListRounded fill="var(--action-active)" />
              </Button>
            </Menu.Target>

            <Menu.Dropdown>
              {sortOptions(t)?.map((s, i) => (
                <Menu.Item
                  onClick={async () => {
                    setSelectedSortBy(s.value);
                    searchParams.set('sort_by', s.value);
                    setParams(searchParams);

                    await refetch();
                    await refetch();
                  }}
                  key={i}
                >
                  {s.label}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>

          <Button
            style={{ flexShrink: 0 }}
            onClick={() => {
              toggleImportMemberOpen();
            }}
            leftSection={<AddRounded fill="var(--white)" />}
          >
            {t('manage_members.button.import_from_csv')}
          </Button>
          <Button
            px={20}
            style={{ flexShrink: 0 }}
            variant="outline"
            // onClick={exportAttendees}
            onClick={toggleExportMemberOpen}
            display={{ base: 'none', sm: 'none', md: 'block' }}
          >
            <DownloadRounded fill="var(--primary-main)" />
            Export List
          </Button>
        </Group>
        {isFiltering && (
          <SearchResult
            total={data?.pages?.[0].total ?? 0}
            resetFilter={resetFilter}
          />
        )}
        {isFiltering && data?.pages?.[0].total === 0 ? (
          <EmptyList
            title={t('community-forum.label.no_result')}
            subtitle={t('manage_members.label.empty_result_subtitle')}
          />
        ) : (
          <>
            <TableComponent {...tableOptions} />
            <PaginationComponent
              total={data?.pages?.[0].total_page ?? 0}
              perPage={parseInt(limit)}
              currentPage={parseInt(page)}
              onHandlePageChange={async (page) => {
                setPage(page.toString());
                await refetch();
                await refetch();
              }}
              onHandleLimitChange={async (limit) => {
                setLimit(limit.toString());
                setPage('1');
                await refetch();
                await refetch();
              }}
            />
          </>
        )}
      </Stack>
    </>
  );
}
